import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate, Link } from "react-router-dom";

const roleHome = (role) => {
  if (role === "client") return "/portal";
  if (role === "tradeline_admin") return "/tradeline-admin";
  return "/admin";
};
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DUCK_LOGO, api, formatApiError } from "@/lib/api";
import SocialAuthButtons from "@/components/app/SocialAuthButtons";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user && user !== false) {
    return <Navigate to={roleHome(user.role)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      nav(roleHome(u.role), { replace: true });
    } catch (e2) {
      setErr(e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left branded panel */}
      <div className="relative hidden md:flex flex-col justify-between p-12 bg-gradient-to-br from-amber-50 via-white to-sky-50 overflow-hidden">
        <div className="cq-grain absolute inset-0" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-3 group" data-testid="login-brand-home">
            <img src={DUCK_LOGO} alt="" className="h-14 w-14 object-contain cq-waddle group-hover:scale-105 transition" />
            <div>
              <p className="font-display font-black text-2xl text-slate-900">Credit Quack</p>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Client Tracker</p>
            </div>
          </Link>
        </div>
        <div className="relative space-y-6 max-w-md">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-amber-600">Financial-tech, without the stiff.</p>
          <h1 className="font-display text-5xl font-black leading-[1.05] text-slate-900">
            Track every client's credit repair journey — like a pizza tracker.
          </h1>
          <p className="text-slate-600 text-lg">Real-time status. Fewer phone calls. Happier clients. It just quacks.</p>
        </div>
        <div className="relative flex items-center gap-2 text-sm text-slate-500">
          <span className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" />
          System healthy · Ready to sign you in
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-white">
        <Card className="w-full max-w-md p-8 rounded-2xl border-slate-100 shadow-sm cq-in">
          <div className="mb-6 md:hidden flex items-center gap-3">
            <img src={DUCK_LOGO} alt="" className="h-12 w-12 object-contain" />
            <div>
              <p className="font-display font-black text-xl text-slate-900">Credit Quack</p>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Client Tracker</p>
            </div>
          </div>
          <h2 className="font-display text-3xl font-black text-slate-900">Sign in</h2>
          <p className="text-slate-500 mt-1 text-sm">Access your admin console or client portal.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" placeholder="you@creditquack.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-amber-600 hover:text-amber-700" data-testid="forgot-password-link">Forgot?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" placeholder="••••••••" />
            </div>
            {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 text-white h-11 rounded-xl" data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <SocialAuthButtons label="or continue with" />

          <p className="mt-4 text-center text-sm text-slate-500">
            New to Credit Quack? <Link to="/register" className="text-amber-600 font-bold" data-testid="register-link">Create an account</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
